import React, { useEffect, useState } from "react";
import { NavLink, Navigate, useParams } from "react-router-dom";
import { FaDollarSign, FaListAlt, FaMedal, FaTable } from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import FinancialTab from "../../components/AdminDash/Tabs/FinancialTab";
import TournamentPayoutTable from "../../components/PayoutTable/TournamentPayoutTable";
import PayoutDashBoard from "../../components/PayoutDashboard/PayoutDashBoard";
import PrizeBreakdownView from "../../components/Prizes/PrizeBreakdownView";
import { getFinanceUrl } from "../../utility/adminNavigation";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";

const FINANCE_TABS = [
  {
    id: "payouts",
    label: "Payouts",
    icon: FaListAlt,
  },
  {
    id: "added-money",
    label: "Added money",
    icon: FaDollarSign,
  },
  {
    id: "payout-table",
    label: "Payout table",
    icon: FaTable,
  },
  {
    id: "prizes",
    label: "Prizes",
    icon: FaMedal,
  },
];

export default function TournamentFinance() {
  const { tab } = useParams();
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const [tournament, setTournament] = useState(null);

  useEffect(() => {
    if (!tournamentId) return;
    let cancelled = false;
    supabase
      .from("tournaments")
      .select("name, payoutRatioNumerator, payoutRatioDenominator")
      .eq("id", tournamentId)
      .single()
      .then(({ data, error }) => {
        if (cancelled || error) return;
        setTournament(data);
      });
    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  if (!tab || tab === "prize-fund") {
    return (
      <Navigate
        to={getFinanceUrl(
          tournamentId,
          tab === "prize-fund" ? "added-money" : "payouts"
        )}
        replace
      />
    );
  }

  if (!FINANCE_TABS.some((item) => item.id === tab)) {
    return <Navigate to={getFinanceUrl(tournamentSlug, "payouts")} replace />;
  }

  const ratioLabel =
    tournament?.payoutRatioNumerator && tournament?.payoutRatioDenominator
      ? `Pays ${tournament.payoutRatioNumerator} in ${tournament.payoutRatioDenominator} bowlers.`
      : null;

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Tournaments", path: "/tournaments" },
    {
      label: tournament?.name || "Tournament",
      path: tournamentPath(tournamentSlug),
    },
    { label: FINANCE_TABS.find((item) => item.id === tab)?.label || "Payouts" },
  ];

  const renderTab = () => {
    if (tab === "payouts") {
      return <PayoutDashBoard tournamentId={tournamentId} />;
    }
    if (tab === "added-money") {
      return <FinancialTab />;
    }
    if (tab === "prizes") {
      return <PrizeBreakdownView tournamentId={tournamentId} embedded />;
    }
    return <TournamentPayoutTable tournamentId={tournamentId} embedded />;
  };

  return (
    <>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="space-y-6">
        <div className="card-board overflow-hidden">
          <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
            <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
              Payouts
            </h1>
            <p className="text-sm text-brand-mist mt-1">
              Payouts, added money, payout table, and prizes.
              {ratioLabel ? ` ${ratioLabel}` : ""}
            </p>
          </div>
          <nav
            className="flex flex-wrap px-2 sm:px-4"
            aria-label="Payouts tabs"
          >
            {FINANCE_TABS.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={getFinanceUrl(tournamentSlug, item.id)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 min-h-11 py-2 px-3 sm:px-4 border-b-2 font-display font-bold text-sm tracking-wide whitespace-nowrap transition-colors duration-200 ${
                      isActive
                        ? "border-brand-charcoal text-brand-charcoal"
                        : "border-transparent text-brand-muted hover:text-brand-charcoal"
                    }`
                  }
                >
                  <Icon className="w-4 h-4 text-brand-muted" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        <div>{renderTab()}</div>
      </div>
    </>
  );
}
